import {CmsApiService} from '../../core/cms-api.service';
import {LoggerService} from '../../core/logger.service';
import {JobStatusSubscriberService} from '../../core/jobstatussubcriber/job-status-subscriber.service';
import {IsActiveService} from '../../main-menu/jobstatus/is-active.service';
import {
  ImportModuleAdminEventRetryResult,
  ImportModuleJobResult,
  ImportModuleRunResult,
  ImportModuleRunSpec,
} from '../../core/definitions/import-module';

/** What a `RunJob` calls out to. */
export interface RunJobDeps {
  cms: Pick<CmsApiService, 'importModuleRun' | 'importModuleJobResult'>;
  jobStatusSubscriber: Pick<JobStatusSubscriberService, 'pollNow'>;
  isActiveService: Pick<IsActiveService, 'changeState'>;
  logger: Pick<LoggerService, 'error'>;
  /** The text shown for a failed run: the rejection's own message, or the
   * generic run error when `e` carries none. */
  errorText: (e: unknown) => string;
  /** Called once the backend has accepted a job. */
  onQueued: () => void;
}

export const RUN_POLL_INTERVAL_MS = 3000;

/** True when `e` is a rejected HTTP response whose 4xx status means the
 * same request will keep failing. 401 means the access token expired and
 * the app's job-status poll refreshes it, and 408 (Request Timeout) and 429
 * (Too Many Requests) mean "retry later", so they return false, as does a
 * rejection without a status or with any status outside 400–499. */
export function isPermanentClientError(e: any): boolean {
  const status = e?.status;
  return typeof status === 'number' && status >= 400 && status < 500
    && status !== 401 && status !== 408 && status !== 429;
}

/** An import run on the backend: enqueues a `/run` spec, polls its job
 * until it finishes or fails, and holds what the run card shows. One poll
 * is on its way at a time: the next is asked for `RUN_POLL_INTERVAL_MS`
 * after the previous answer, and an answer for a job no longer running is
 * dropped. Row and admin-event retries resubmit the last spec. */
export class RunJob {
  running = false;
  runResult: ImportModuleRunResult | null = null;
  runError: string | null = null;
  runProgress: number | null = null;
  adminEventRetryResult: ImportModuleAdminEventRetryResult | null = null;
  private runJobId: string | null = null;
  private runPollTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRunSpec: ImportModuleRunSpec | null = null;
  // Set for the duration of a `retry_admin_events` job so `pollRunJob`
  // knows to route the (differently-shaped) summary into
  // `adminEventRetryResult` instead of `runResult`.
  private isAdminEventRetryJob = false;
  // Set by `dispose`: an answer arriving later starts no timer.
  private disposed = false;

  constructor(private readonly deps: RunJobDeps) {}

  /** The source column the last submitted run joined media on, from its
   * own spec; `null` when it joined none or nothing was submitted. */
  get mediaJoinColumn(): string | null {
    return this.lastRunSpec?.media_join_column ?? null;
  }

  /** Enqueues `spec` as a new run; `runResult` fills in once polling sees
   * the job finish. */
  submit(spec: ImportModuleRunSpec): Promise<void> {
    return this.submitRunSpec(spec);
  }

  /** Drops the shown result and error. */
  clearResult(): void {
    this.runResult = null;
    this.runError = null;
  }

  /** Stops polling; a job answer that arrives later starts no timer. */
  dispose(): void {
    this.disposed = true;
    this.stopPolling();
  }

  /** Resubmits the last run's spec restricted to every clean-failure row id
   * — `orphan_failures` are never included (`ImportModuleWriter` already
   * committed a shell for those; retrying would create a second one). Reads
   * `clean_failure_row_ids` (uncapped) rather than mapping `clean_failures`
   * (capped server-side to the first ~200 shown), so a run with more
   * failures than the display cap still retries all of them.
   * The mapping/batch config is reused as-is from the run just done, not
   * rebuilt from the current form state, so this only makes sense
   * immediately after a run against the same file. */
  async retryFailedRows(): Promise<void> {
    const failedRowIds = this.runResult?.clean_failure_row_ids ?? [];
    if (!this.lastRunSpec || !failedRowIds.length || this.running) {
      return;
    }
    await this.submitRunSpec({
      ...this.lastRunSpec,
      only_row_ids: failedRowIds,
      parent_job_id: this.runJobId ?? undefined,
    });
  }

  /** Re-attempts every currently-listed `admin_event_failures` entry via
   * `spec.retry_admin_events` — unlike `retryFailedRows`, this never
   * recreates a row's own artifact, only the missing secondary admin-event
   * artifact for each one. Only the shown (capped) list is ever sent:
   * there's no uncapped id list for admin-event failures the way
   * `clean_failure_row_ids` exists for `clean_failures`. */
  async retryAdminEvents(): Promise<void> {
    const entries = this.runResult?.admin_event_failures ?? [];
    if (!this.lastRunSpec || !entries.length || this.running) {
      return;
    }
    await this.submitRunSpec({
      ...this.lastRunSpec,
      retry_admin_events: entries.map(f => (
        {artifact_id: f.artifact_id, group_key: f.group_key, values: f.values})),
      parent_job_id: this.runJobId ?? undefined,
    }, true);
  }

  /** Folds a finished `retry_admin_events` job's result back onto the
   * still-displayed `runResult`: drops every entry that succeeded (by
   * `artifact_id` + `group_key`) so a second retry click can't re-send an
   * admin event that was already created, and refreshes the message on
   * any entry still failing. */
  private applyAdminEventRetryResult(result: ImportModuleAdminEventRetryResult): void {
    if (!this.runResult) {
      return;
    }
    const priorTotal = this.runResult.admin_event_failures_total;
    const stillFailing = new Map(
      result.failures.map(f => [`${f.artifact_id}|${f.group_key}`, f.message]));
    this.runResult.admin_event_failures = this.runResult.admin_event_failures
      .filter(f => stillFailing.has(`${f.artifact_id}|${f.group_key}`))
      .map(f => ({...f, message: stillFailing.get(`${f.artifact_id}|${f.group_key}`)!}));
    this.runResult.admin_event_failures_total = Math.max(0, priorTotal - result.succeeded);
  }

  /** Enqueues `spec` via `POST /run` (fast — validation only) and polls
   * `GET /jobs/<id>` until the job finishes or fails. `isAdminEventRetry`
   * tells `pollRunJob` where to route the finished summary, since a
   * `retry_admin_events` job's summary is a different shape and, unlike a
   * normal run or row retry, doesn't replace the still-displayed
   * `runResult`. */
  private async submitRunSpec(spec: ImportModuleRunSpec, isAdminEventRetry = false): Promise<void> {
    this.stopPolling();
    this.lastRunSpec = spec;
    this.running = true;
    this.isAdminEventRetryJob = isAdminEventRetry;
    this.adminEventRetryResult = null;
    if (!isAdminEventRetry) {
      this.runResult = null;
    }
    this.runError = null;
    this.runProgress = null;
    this.runJobId = null;
    try {
      const {job_id} = await this.deps.cms.importModuleRun(spec);
      this.runJobId = job_id;
      // Surface the queued job in the shared "Dine operasjoner" panel -
      // the async run outlives this page, so it must be trackable after a
      // navigation away. An immediate poll makes the panel react before a
      // short job finishes.
      this.deps.jobStatusSubscriber.pollNow();
      this.deps.isActiveService.changeState(true);
      this.deps.onQueued();
      // Await the first check rather than waiting a full interval for it -
      // a fast job (or a test's mocked response) can resolve `submit()`
      // with `runResult` already set. The next poll is timed from its
      // answer, and only if the job is still going.
      await this.pollRunJob(job_id);
      this.armIfLive(job_id);
    } catch (e: any) {
      this.deps.logger.error(`raw import: enqueue failed: ${e}`);
      // The backend's 400 message (e.g. the media block reason) is the
      // useful text; the generic one only if it's missing.
      this.runError = this.deps.errorText(e);
      this.running = false;
    }
  }

  private stopPolling(): void {
    if (this.runPollTimer !== null) {
      clearTimeout(this.runPollTimer);
      this.runPollTimer = null;
    }
  }

  /** Polls `jobId` again after `RUN_POLL_INTERVAL_MS`, while it is the
   * job running and the run is not disposed. */
  private armIfLive(jobId: string): void {
    if (this.running && !this.disposed && jobId === this.runJobId) {
      this.scheduleNextPoll(jobId);
    }
  }

  /** Polls `jobId` after `RUN_POLL_INTERVAL_MS`, and once it has answered,
   * schedules the next poll the same way - after a transient error too. */
  private scheduleNextPoll(jobId: string): void {
    this.runPollTimer = setTimeout(async () => {
      await this.pollRunJob(jobId);
      this.armIfLive(jobId);
    }, RUN_POLL_INTERVAL_MS);
  }

  private async pollRunJob(jobId: string): Promise<void> {
    if (jobId !== this.runJobId) {
      return;  // superseded by a newer run/retry started while this poll was pending
    }
    let job: ImportModuleJobResult;
    try {
      job = await this.deps.cms.importModuleJobResult(jobId);
    } catch (e: any) {
      this.deps.logger.error(`raw import: job poll failed: ${e}`);
      if (jobId === this.runJobId && this.running && isPermanentClientError(e)) {
        // The backend answers 4xx for a job it no longer knows (deleted, or
        // another user's), so a later poll would get the same answer.
        this.stopPolling();
        this.running = false;
        this.runError = this.deps.errorText(e);
      }
      // A network error, a 5xx, a 401, a 408 or a 429 is transient — the next poll tries again.
      return;
    }
    if (jobId !== this.runJobId || !this.running) {
      return;  // superseded, or the job's end already shown, while the request was in flight
    }
    this.runProgress = job.progress;
    if (job.status !== 'finished' && job.status !== 'failed') {
      return;
    }
    this.stopPolling();
    this.running = false;
    if (job.status === 'finished' && job.summary && this.isAdminEventRetryJob) {
      this.adminEventRetryResult = job.summary as ImportModuleAdminEventRetryResult;
      this.applyAdminEventRetryResult(this.adminEventRetryResult);
    } else if (job.status === 'finished' && job.summary) {
      this.runResult = job.summary as ImportModuleRunResult;
    } else {
      this.runError = job.error_message || this.deps.errorText(null);
    }
  }
}
